import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import TextField from '@mui/material/TextField'
import { useTranslation } from 'next-i18next'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { TEXT_FIELD_MULTILINE_SX } from 'src/constants/theme'
import useErrorSnackbar from 'src/hooks/useErrorSnackbar'
import ga from 'src/utils/pixels/gtag'
import { IMutation, IMutationReportLocationArgs } from '../../types/graphql'
import BaseButton from '../Buttons/BaseButton'
import ModalDrawer from '../ModalDrawer'

const REPORT_LOCATION = gql`
  mutation ReportLocation($locationId: String!, $reason: String!) {
    reportLocation(locationId: $locationId, reason: $reason) {
      id
    }
  }
`

type IProps = {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  locationId: string
}

export default function ReportLocationModal({
  locationId,
  isOpen,
  setIsOpen
}: IProps) {
  const [message, setMessage] = useState<string>('')
  const { t: tDetails } = useTranslation('details')

  const { enqueueSnackbar } = useSnackbar()
  const enqueueError = useErrorSnackbar()

  // Fire once each time the modal becomes visible. Pairs with
  // `report_location_submit` (fired in handleReportLocation) so PostHog can
  // compute open→submit conversion. Sensitive surface — keep params minimal.
  useEffect(() => {
    if (!isOpen) return
    ga.event('report_location_open', { listing_id: locationId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const [reportLocation, { loading }] = useMutation<
    Pick<IMutation, 'reportLocation'>,
    IMutationReportLocationArgs
  >(REPORT_LOCATION, {
    onCompleted: () => {
      enqueueSnackbar('Alojamiento reportado.', { variant: 'warning' })
      setMessage('')
      setIsOpen(false)
    },
    onError: (error) => {
      enqueueError(error, { logMessage: 'Error reporting location' })
    }
  })

  const handleReportLocation = () => {
    ga.event('report_location_submit', { listing_id: locationId })
    reportLocation({ variables: { locationId, reason: message } })
  }

  return (
    <ModalDrawer
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={tDetails('REPORT_MODAL.TITLE')}
    >
      <div className="flex w-full flex-col items-center justify-center gap-2">
        <div className="flex w-full flex-col gap-2 text-start">
          <h3 className="text-[18px] leading-normal font-semibold text-ink-700">
            ¿Por qué estas reportando este alojamiento?
          </h3>

          <p className="text-base leading-normal font-medium text-ink-200">
            Esto no sera compartido con el anfitrión.
          </p>
        </div>

        <TextField
          multiline
          fullWidth
          id="phone"
          label={tDetails('REPORT_MODAL.MESSAGE_LABEL')}
          name="message"
          autoComplete="message"
          rows={4}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          sx={TEXT_FIELD_MULTILINE_SX}
        />

        <BaseButton
          onClick={handleReportLocation}
          loading={loading}
          variant="contained"
          fullWidth
          rounded
        >
          Reportar este alojamiento
        </BaseButton>
      </div>
    </ModalDrawer>
  )
}
